
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, LineChart, PieChart, Calendar, Download, FileText, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const SustainabilityDashboard = () => {
  return (
    <div className="container mx-auto px-4 py-6 space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Sustainability Dashboard</h1>
          <p className="text-muted-foreground">Monitor and improve your environmental impact metrics</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex items-center gap-1">
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Link to="/settings">
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              <Settings className="h-4 w-4" />
              Settings
            </Button>
          </Link>
        </div>
      </div>

      <Tabs defaultValue="carbon" className="space-y-6">
        <TabsList className="w-full sm:w-auto grid grid-cols-3 gap-1 sm:inline-flex">
          <TabsTrigger value="carbon" className="flex items-center gap-1">
            <BarChart className="h-4 w-4" />
            <span className="sm:inline">Carbon Footprint</span>
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            <span className="sm:inline">ESG Reports</span>
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span className="sm:inline">Compliance Calendar</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="carbon" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Carbon Emissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">125.4 tCO2e</div>
                <p className="text-xs text-muted-foreground">-12.5% from last month</p>
                <div className="mt-4 h-32 w-full bg-blue-50 dark:bg-blue-950 rounded-md flex items-center justify-center text-muted-foreground">
                  <LineChart className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Energy Consumption</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">458.7 MWh</div>
                <p className="text-xs text-muted-foreground">+2.3% from last month</p>
                <div className="mt-4 h-32 w-full bg-amber-50 dark:bg-amber-950 rounded-md flex items-center justify-center text-muted-foreground">
                  <BarChart className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Emission Sources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mt-4 h-32 w-full bg-green-50 dark:bg-green-950 rounded-md flex items-center justify-center text-muted-foreground">
                  <PieChart className="h-6 w-6" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span>Electricity (42%)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span>Natural Gas (28%)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span>Transportation (18%)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span>Other (12%)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Carbon Reduction Initiatives</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border rounded-md p-4">
                  <h3 className="font-medium">Solar Panel Installation</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Projected to reduce carbon emissions by 35 tCO2e annually.
                  </p>
                  <div className="mt-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5">
                    <div className="bg-green-600 h-2.5 rounded-full w-[65%]"></div>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span>Progress: 65%</span>
                    <span>Completion: Q3 2025</span>
                  </div>
                </div>

                <div className="border rounded-md p-4">
                  <h3 className="font-medium">Energy Efficiency Program</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Projected to reduce carbon emissions by 18 tCO2e annually.
                  </p>
                  <div className="mt-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5">
                    <div className="bg-blue-600 h-2.5 rounded-full w-[40%]"></div>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span>Progress: 40%</span>
                    <span>Completion: Q4 2025</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>ESG Report History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border rounded-md p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div>
                    <h3 className="font-medium">Annual Sustainability Report 2024</h3>
                    <p className="text-sm text-muted-foreground">Published on April 15, 2024</p>
                  </div>
                  <Button variant="outline" size="sm" className="flex items-center gap-1">
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                </div>
                
                <div className="border rounded-md p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div>
                    <h3 className="font-medium">Q1 2024 Carbon Disclosure</h3>
                    <p className="text-sm text-muted-foreground">Published on February 10, 2024</p>
                  </div>
                  <Button variant="outline" size="sm" className="flex items-center gap-1">
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                </div>
                
                <div className="border rounded-md p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div>
                    <h3 className="font-medium">Annual Sustainability Report 2023</h3>
                    <p className="text-sm text-muted-foreground">Published on April 10, 2023</p>
                  </div>
                  <Button variant="outline" size="sm" className="flex items-center gap-1">
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Compliance Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border rounded-md p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <h3 className="font-medium">Carbon Disclosure Project Submission</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Due: July 31, 2025</p>
                  <p className="text-sm mt-2">
                    Annual submission of carbon emissions data and reduction strategies to CDP.
                  </p>
                </div>
                
                <div className="border rounded-md p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <h3 className="font-medium">Quarterly ESG Committee Meeting</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Due: June 15, 2025</p>
                  <p className="text-sm mt-2">
                    Quarterly review of sustainability initiatives and progress against goals.
                  </p>
                </div>
                
                <div className="border rounded-md p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <h3 className="font-medium">Annual Sustainability Report Preparation</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Due: March 1, 2025</p>
                  <p className="text-sm mt-2">
                    Begin data collection and drafting for the annual sustainability report.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SustainabilityDashboard;
